
import { useState } from "react"

import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
// Add the Plus icon to the import from lucide-react
import { Plus, Trash2 } from "lucide-react"

export interface Installment {
  amount: number
  date: string
}

interface InstallmentInputProps {
  value: Installment[]
  onChange: (value: Installment[]) => void
  suggestedAmount?: number
}

export function InstallmentInput({ value, onChange, suggestedAmount = 0 }: InstallmentInputProps) {
  const { t } = useTranslation()
  const [showEmptyMessage, setShowEmptyMessage] = useState(false)

  const handleAddInstallment = () => {
    const newInstallment: Installment = {
      amount: suggestedAmount > 0 ? suggestedAmount : 0,
      date: new Date().toISOString().split("T")[0],
    }
    onChange([...value, newInstallment])
    setShowEmptyMessage(false)
  }

  const handleRemoveInstallment = (index: number) => {
    const newInstallments = [...value]
    newInstallments.splice(index, 1)
    onChange(newInstallments)
    setShowEmptyMessage(newInstallments.length === 0)
  }

  const handleInstallmentChange = (index: number, field: keyof Installment, newValue: any) => {
    const newInstallments = [...value]
    newInstallments[index] = {
      ...newInstallments[index],
      [field]: newValue,
    }
    onChange(newInstallments)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label>{t("payments.installments", "Installments")}</Label>
      </div>

      {value.length === 0 && showEmptyMessage && (
        <div className="text-center py-4 text-muted-foreground">
          {t("payments.noInstallments", "No installments added yet")}
        </div>
      )}

      {value.length > 0 && (
        <div className="space-y-3">
          {value.map((installment, index) => (
            <div key={index} className="flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor={`amount-${index}`} className="text-xs">
                  {t("common.amount", "Amount")}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2">$</span>
                  <Input
                    id={`amount-${index}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={installment.amount}
                    onChange={(e) => handleInstallmentChange(index, "amount", Number.parseFloat(e.target.value) || 0)}
                    className="pl-7"
                  />
                </div>
              </div>
              <div className="flex-1">
                <Label htmlFor={`date-${index}`} className="text-xs">
                  {t("payments.date", "Date")}
                </Label>
                <Input
                  id={`date-${index}`}
                  type="date"
                  value={
                    installment.date
                      ? new Date(installment.date).toISOString().split("T")[0]
                      : new Date().toISOString().split("T")[0]
                  }
                  onChange={(e) => handleInstallmentChange(index, "date", e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveInstallment(index)}
                className="h-10 w-10"
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">{t("common.delete", "Delete")}</span>
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button type="button" variant="outline" className="w-full" onClick={() => handleAddInstallment()}>
        <Plus className="mr-2 h-4 w-4" />
        {t("payments.addInstallment", "Add Installment")}
      </Button>
    </div>
  )
}
